<template>
  <v-dialog persistent scrollable v-model="dialog" max-width="720">
    <v-card :loading="loading">
      <template slot="progress">
        <v-progress-linear indeterminate color="primary" />
      </template>
      <v-card-title class="headline">Breeds resolving</v-card-title>
      <v-card-subtitle>Changes will be saved automatically</v-card-subtitle>
      <v-divider />
      <v-card-text class="pt-5">
        <v-row class="ma-0 breed-item" :key="breed.id" v-for="breed of breeds">
          <v-col class="pl-0" cols="3">
            <p class="ma-0 body-1">{{ breed.original_name }}</p>
            <p class="ma-0 body-2">{{ breed.name }}</p>
          </v-col>
          <v-col cols="4">
            <v-autocomplete
              dense
              label="Select species"
              prepend-icon="mdi-arrow-right"
              item-text="name"
              item-value="name"
              :disabled="isReadonly"
              :no-data-text="
                rhapsodyBreedsLoading ? 'Loading...' : 'No data available'
              "
              :items="rhapsodySpecies"
              :loading="rhapsodyBreedsLoading"
              v-model="breed.species"
              @change="(species) => handleBreedChange(breed.id, { species })"
            />
          </v-col>
          <v-col class="pr-0" cols="5">
            <v-autocomplete
              dense
              label="Select breed"
              prepend-icon="mdi-arrow-right"
              item-text="name"
              item-value="internalCode"
              :disabled="isReadonly"
              :no-data-text="
                rhapsodyBreedsLoading ? 'Loading...' : 'No data available'
              "
              :items="getBreedsItems(breed.species)"
              :loading="rhapsodyBreedsLoading"
              v-model="breed.breed_id"
              @change="
                (breedId) => handleBreedChange(breed.id, { breed_id: breedId })
              "
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn disabled text small v-if="breedUpdating">
          <v-icon left>mdi-content-save-outline</v-icon>
          Saving...
        </v-btn>
        <v-spacer />
        <v-btn text color="accent" @click="handleClose">Cancel</v-btn>
        <v-btn
          text
          color="success"
          :disabled="isReadonly"
          :loading="rhapsodyBreedsLoading"
          @click="handleResolve"
        >
          Resolve
        </v-btn>
        <slot name="action" />
      </v-card-actions>
    </v-card>
    <ConfirmationDialog
      ref="confirmationDialogRef"
      @onConfirm="handleResolvingConfirm"
    />
    <v-snackbar light left timeout="2000" v-model="breedChangesSavedSnackbar">
      Breed changes saved
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { fetchRhapsodyConstants } from '@/api';
import { breedsApiV1 } from '@/api/v1';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import { breedsApiV2 } from '@/api/v2';

const BREEDS_CONSTANT_NAME = 'Breed';
const SPECIES_CONSTANT_NAME = 'Species';

export default {
  props: {
    breedStateId: {
      type: Number,
      default: null
    },
    isReadonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      breeds: [],
      rhapsodyBreedsLoading: false,
      rhapsodySpecies: [],
      rhapsodyBreeds: {},
      rhapsodyBreedsList: [],
      breedUpdating: false,
      breedChangesSavedSnackbar: false
    };
  },
  components: {
    ConfirmationDialog
  },
  methods: {
    open() {
      this.dialog = true;
      this.init();
    },
    async init() {
      this.loading = true;
      this.breeds = await (
        this.$store.getters.isMatcherApiV2Enabled ? breedsApiV2 : breedsApiV1
      ).fetchBreeds(this.breedStateId);
      this.loading = false;
      await this.getRhapsodyBreeds();
    },
    async getRhapsodyBreeds() {
      if (!this.rhapsodyBreedsList.length) {
        this.rhapsodyBreedsLoading = true;
        const constants = await fetchRhapsodyConstants();
        this.rhapsodyBreeds = constants[BREEDS_CONSTANT_NAME];
        this.rhapsodySpecies = constants[SPECIES_CONSTANT_NAME];

        this.rhapsodyBreedsList = this.rhapsodySpecies.reduce(
          (rhapsodyBreedsList, { id, humanReadable }) => {
            const speciesBreeds = this.rhapsodyBreeds[id] || [];
            const speciesBreedsWithGroup = speciesBreeds.map((breed) => ({
              group: humanReadable,
              ...breed
            }));

            return [
              ...rhapsodyBreedsList,
              { header: humanReadable },
              ...speciesBreedsWithGroup,
              { divider: true }
            ];
          },
          []
        );
        this.rhapsodyBreedsLoading = false;
      }
    },
    handleClose() {
      this.dialog = false;
      this.loading = false;
      this.rhapsodyBreedsLoading = false;
    },
    async handleBreedChange(id, updated) {
      this.breedUpdating = true;
      await (
        this.$store.getters.isMatcherApiV2Enabled ? breedsApiV2 : breedsApiV1
      ).updateBreed(id, updated);
      this.breedUpdating = false;
      this.breedChangesSavedSnackbar = true;
    },
    handleResolve() {
      this.$refs.confirmationDialogRef.open({
        title: 'Resolve breeds mappings?',
        subtitle: 'After resolving, you will not be able to change mappings.',
        width: 480
      });
    },
    async handleResolvingConfirm() {
      this.loading = true;
      await (
        this.$store.getters.isMatcherApiV2Enabled ? breedsApiV2 : breedsApiV1
      ).resolveBreeds(this.breedStateId);
      this.$emit('onBreedsResolved');
      this.handleClose();
    },
    getBreedsItems(speciesName) {
      const species = this.rhapsodySpecies.find(
        ({ name }) => name === speciesName
      );

      return species
        ? this.rhapsodyBreeds[species.id]
        : this.rhapsodyBreedsList;
    }
  }
};
</script>

<style lang="scss">
.breed-item {
  border-bottom: 1px solid #eeeeee;
  &:last-of-type {
    border-bottom: 0;
  }
}
</style>
